import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ThemeToggle } from '../../components/ui/ThemeToggle';
import { useScenarios } from '../../lib/useScenarios';
import styles from './DashboardPage.module.css';

export function DashboardPage(): React.JSX.Element {
  const { user } = useAuth();
  const { scenarios } = useScenarios();

  const total = scenarios.length;
  const draft = scenarios.filter((s) => s.status === 'draft').length;
  const finalized = scenarios.filter((s) => s.status === 'finalized').length;

  const firstName = user?.displayName.split(' ')[0] ?? 'there';

  return (
    <>
      <PageHeader title="Dashboard" meta="Overview" actions={<ThemeToggle />} />

      <div className={styles.body}>
        <h2 className={styles.welcome}>Welcome back, {firstName}! 👋</h2>
        <p className={styles.subtitle}>
          Here's an overview of your Microsoft Commerce Incentives workspace.
        </p>

        <p className={styles.sectionLabel}>Active modules</p>
        <div className={styles.grid}>
          <Card accent className={styles.moduleCard}>
            <div className={styles.moduleHead}>
              <span className={styles.moduleIcon} aria-hidden="true">
                <CoinsIcon />
              </span>
              <div className={styles.moduleTitleBlock}>
                <div className={styles.moduleTitleRow}>
                  <p className={styles.moduleTitle}>MCI Engagement Finder</p>
                  <Badge tone="ok" dot>
                    Active
                  </Badge>
                </div>
                <p className={styles.moduleCategory}>Microsoft Commerce Incentives</p>
              </div>
            </div>

            <p className={styles.moduleDesc}>
              Work out which Microsoft engagements a deal can claim, and what each pays, from a
              customer's Azure consumption, seats and pre-qualification answers.
            </p>

            <div className={styles.statRow}>
              <div className={styles.stat}>
                <span className={styles.statValue} style={{ color: 'var(--primary)' }}>
                  {total}
                </span>
                <span className={styles.statLabel}>Total scenarios</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue} style={{ color: 'var(--warn)' }}>
                  {draft}
                </span>
                <span className={styles.statLabel}>Draft</span>
              </div>
              <div className={styles.stat}>
                <span className={styles.statValue} style={{ color: 'var(--ok)' }}>
                  {finalized}
                </span>
                <span className={styles.statLabel}>Finalized</span>
              </div>
            </div>

            <Link to="/scenarios" style={{ textDecoration: 'none' }}>
              <Button variant="primary" style={{ width: '100%' }}>
                <DocIcon /> Open MCI Engagement Finder
              </Button>
            </Link>
          </Card>
        </div>

        <p className={styles.sectionLabel}>Coming soon</p>
        <div className={styles.comingSoonGrid}>
          <Card className={styles.comingSoonCard}>
            <span className={styles.comingSoonIcon} style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>
              <PromoIcon />
            </span>
            <p className={styles.moduleTitle}>CSP Promotions</p>
            <p className={styles.comingSoonBody}>
              Customer discounts from the Microsoft promotions calendar, and what each one costs
              against an MCI engagement.
            </p>
          </Card>
          <Card className={styles.comingSoonCard}>
            <span
              className={styles.comingSoonIcon}
              style={{ background: 'var(--ok-bg)', color: 'var(--ok)' }}
            >
              <ChartIcon />
            </span>
            <p className={styles.moduleTitle}>Analytics &amp; Reporting</p>
            <p className={styles.comingSoonBody}>
              Claimable totals, win rates and nomination history across every consultant's
              scenarios.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

function CoinsIcon(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12.5 12.2A5.5 5.5 0 1 0 9.8 15.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <path d="M16 10.5A5.5 5.5 0 1 1 10.5 16" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

function DocIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 1.5H9.5L13 5V13.5A1 1 0 0 1 12 14.5H4A1 1 0 0 1 3 13.5V2.5A1 1 0 0 1 4 1.5Z"
        stroke="currentColor"
        strokeWidth="1.4"
      />
      <path d="M9.5 1.5V5H13" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function PromoIcon(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3L14.4 8.5L20.5 9.2L16 13.2L17.2 19.3L12 16.2L6.8 19.3L8 13.2L3.5 9.2L9.6 8.5L12 3Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChartIcon(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M4 20V10M12 20V4M20 20V14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
